import { useQuery } from '@tanstack/react-query';
import type { ReviewQueueItem } from '../../types';
import { questionsService } from '../../../../services/xano/questions';
import { questionTypesService } from '../../../../services/xano/questionTypes';
import { modelSelectionsService } from '../../../../services/xano/modelSelections';
import { xanoClient } from '../../../../services/xano/client';
import type { Question, QuestionType } from '../../../../shared/types';
import { QUESTION_TYPES } from '../../../../shared/constants';

// Mock data generator (keeping for fallback)
const generateMockReviewItem = (index: number): ReviewQueueItem => {
  const priorities: ReviewQueueItem['priority'][] = ['high', 'medium', 'low'];
  
  const questionType = QUESTION_TYPES[index % QUESTION_TYPES.length];
  
  return {
    id: `review_${Date.now()}_${index}`,
    question: {
      id: `q_${Date.now()}_${index}`,
      questionText: `A ${65 + (index % 20)}-year-old patient presents with ${['chest pain', 'shortness of breath', 'abdominal pain', 'headache', 'fatigue'][index % 5]}. The patient has a history of ${['hypertension', 'diabetes', 'COPD', 'heart failure', 'kidney disease'][index % 5]}. What is the most appropriate next step in management?`,
      questionType: questionType.value as any,
      difficultyLevel: ((index % 5) + 1) as any,
      answerOptions: [
        {
          id: 'opt1',
          text: 'Order immediate diagnostic tests',
          isCorrect: index % 4 === 0,
          explanation: 'This option addresses the immediate diagnostic needs.',
        },
        {
          id: 'opt2',
          text: 'Start empirical treatment',
          isCorrect: index % 4 === 1,
          explanation: 'Early treatment can be beneficial in certain conditions.',
        },
        {
          id: 'opt3',
          text: 'Admit for observation',
          isCorrect: index % 4 === 2,
          explanation: 'Observation allows for monitoring of progression.',
        },
        {
          id: 'opt4',
          text: 'Refer to specialist',
          isCorrect: index % 4 === 3,
          explanation: 'Specialist consultation may be warranted.',
        },
      ],
      detailedExplanation: 'This question tests understanding of clinical decision-making in common presentations.',
      aiModel: 'claude-sonnet-4',
      requiresImage: index % 3 === 0,
      generationCost: 0.015,
      createdAt: new Date(Date.now() - index * 24 * 60 * 60 * 1000),
      createdBy: 'ai_generator',
      status: 'draft',
    },
    priority: priorities[index % 3],
    status: index < 5 ? 'pending' : index < 8 ? 'in_progress' : 'completed',
    assignedTo: index % 2 === 0 ? 'Dr. Smith' : undefined,
    dueDate: new Date(Date.now() + (2 - index) * 24 * 60 * 60 * 1000),
    reviews: [],
  };
};

// Fetch function for React Query
const fetchReviewQueue = async (filter: 'all' | 'pending' | 'in_progress', page: number) => {
  // Map filter to Xano status
  let status: string | undefined;
  if (filter === 'pending') status = 'pending';
  else if (filter === 'in_progress') status = 'in_review';
  
  // Fetch questions from Xano with pagination
  const response = await questionsService.queryQuestions(status, page);
  const questions = response.items;
  
  // Get question types mapping
  let typeMap = new Map();
  try {
    const questionTypes = await questionTypesService.getQuestionTypes();
    typeMap = new Map(questionTypes.map(t => [t.id, t]));
  } catch (err) {
    console.error('Failed to fetch question types:', err);
    // Continue without type mapping
  }
  
  // We'll use the nested data from the API response instead of fetching separately
  let examTypes = new Map();
  let examSteps = new Map();
  let questionPools = new Map();
  
  // Transform Xano questions to ReviewQueueItems
  // Also filter to ensure we only show pending questions
  const items: ReviewQueueItem[] = questions
    .filter(q => {
      // Only include questions with pending status
      if (filter === 'pending') {
        return q.review_status === 'pending';
      }
      return true;
    })
    .map((xanoQuestion) => {
    // Map question type ID to our internal type
    // Database IDs:
    // 1: Single Choice, 2: Multiple Select, 3: Fill Blanks, 4: Single Choice (4 opts)
    // 5: Single Choice (6 opts), 6: Clinical Data Table, 7: Lab Values Format
    // 8: Image Based, 9: Clinical Scenario, 10: Management Steps
    const questionTypeIdMap: Record<number, QuestionType> = {
      1: 'single_choice',
      2: 'multiple_select', 
      3: 'fill_blanks',
      4: 'single_choice',  // Single Choice (4 Options) 
      5: 'single_choice',  // Single Choice (6 Options)
      6: 'lab_values',     // Clinical Data Table
      7: 'lab_values',     // Lab Values Format
      8: 'image_based',
      9: 'clinical_scenario',
      10: 'management_steps'
    };
    
    let questionType: QuestionType = questionTypeIdMap[xanoQuestion.question_type_id || 1] || 'single_choice';
    
    // Get model info from question_object if available
    const aiModel = xanoQuestion.question_object?.metadata?.model || 'unknown';
    
    // Map Xano status to our internal status
    let itemStatus: 'pending' | 'in_progress' | 'completed' = 'pending';
    if (xanoQuestion.review_status === 'in_review') itemStatus = 'in_progress';
    else if (xanoQuestion.review_status === 'approved' || xanoQuestion.review_status === 'rejected') itemStatus = 'completed';
    
    // Extract medical tags with better fallback handling
    let medicalTags: any[] = [];
    if (xanoQuestion.medical_tags_id && Array.isArray(xanoQuestion.medical_tags_id)) {
      medicalTags = xanoQuestion.medical_tags_id.map((tagItem: any) => ({
        id: tagItem.medical_tag?.id || tagItem.id,
        name: tagItem.medical_tag?.tag_name || tagItem.name || tagItem.tag_name,
        color_code: tagItem.medical_tag?.color_code || tagItem.color_code
      }));
    }
    
    // Extract answer options and question type from question_object if available
    let answerOptions = [];
    let apiQuestionType = null;
    if (xanoQuestion.question_object && typeof xanoQuestion.question_object === 'object') {
      // Check if content exists within question_object (new batch format)
      if (xanoQuestion.question_object.content) {
        answerOptions = xanoQuestion.question_object.content.answerOptions || [];
        apiQuestionType = xanoQuestion.question_object.content.questionType;
      } else {
        // Fallback to direct properties (old format)
        answerOptions = xanoQuestion.question_object.answerOptions || [];
        apiQuestionType = xanoQuestion.question_object.questionType;
      }
    }
    
    // Use question type from API if available, otherwise fall back to mapping
    if (apiQuestionType) {
      questionType = apiQuestionType as QuestionType;
    }
    
    // Extract structured data if available
    const structuredData = xanoQuestion.question_object?.content?.structuredData || xanoQuestion.question_object?.structuredData;
    const followUpQuestion = xanoQuestion.question_object?.content?.followUpQuestion;
    
    // Get exam info from nested data
    let examTypeName = '';
    let examStepName = '';
    let questionPoolName = '';
    
    
    // Check if exam data is nested in the response
    if (xanoQuestion._exam_steps) {
      examStepName = xanoQuestion._exam_steps.name || '';
      if (xanoQuestion._exam_steps._exam_types) {
        examTypeName = xanoQuestion._exam_steps._exam_types.name || '';
      }
    } else {
      // Fallback to looking up from our fetched data
      if (xanoQuestion.exam_step_id) {
        const examStep = examSteps.get(xanoQuestion.exam_step_id);
        if (examStep) {
          examStepName = examStep.name;
          const examType = examTypes.get(examStep.exam_type_id);
          if (examType) {
            examTypeName = examType.name;
          }
        }
      }
    }
    
    // Get question pool name - check for nested data first
    if (xanoQuestion._question_pools) {
      questionPoolName = xanoQuestion._question_pools.name || '';
    } else if (xanoQuestion._question_pool) {
      questionPoolName = xanoQuestion._question_pool.name || '';
    } else {
      // For now, just show the pool ID if no nested data
      questionPoolName = xanoQuestion.question_pool_id ? `Pool ${xanoQuestion.question_pool_id}` : '';
    }
    
    // Create Question object with exam names
    const question: Question & { 
      examTypeName?: string; 
      examStepName?: string; 
      questionPoolName?: string; 
    } = {
      id: xanoQuestion.id || `q_${Date.now()}`,
      questionText: xanoQuestion.question_text,
      questionType,
      difficultyLevel: xanoQuestion.difficulty_level as any,
      answerOptions,
      detailedExplanation: xanoQuestion.detailed_explanation,
      aiModel: aiModel as any,
      requiresImage: xanoQuestion.requires_image,
      generationCost: xanoQuestion.generation_cost,
      createdAt: xanoQuestion.created_at ? new Date(xanoQuestion.created_at) : new Date(),
      createdBy: 'ai_generator',
      status: 'draft',
      medicalTagIds: medicalTags.map(t => t.id),
      medicalTags,
      // Add exam-related metadata for the new system
      examStepId: xanoQuestion.exam_step_id,
      questionPoolId: xanoQuestion.question_pool_id,
      topicId: xanoQuestion.topic_id,
      // Add exam names
      examTypeName,
      examStepName,
      questionPoolName,
      tokenUsage: {
        input: xanoQuestion.question_object?.tokens?.input || 0,
        output: xanoQuestion.question_object?.tokens?.output || 0,
        total: xanoQuestion.question_object?.tokens?.total || xanoQuestion.token_usage || 0
      },
      ...(structuredData && { structuredData }),
      ...(followUpQuestion && { followUpQuestion })
    };
    
    // Determine priority based on difficulty and age
    const ageInDays = (Date.now() - (xanoQuestion.created_at || 0)) / (1000 * 60 * 60 * 24);
    let priority: 'high' | 'medium' | 'low' = 'medium';
    if (xanoQuestion.difficulty_level >= 4 || ageInDays > 3) priority = 'high';
    else if (xanoQuestion.difficulty_level <= 2) priority = 'low';
    
    // Create ReviewQueueItem
    return {
      id: `review_${xanoQuestion.id}`,
      question,
      priority,
      status: itemStatus,
      assignedTo: xanoQuestion.reviewer_id && xanoQuestion.reviewer_id > 0 ? `Reviewer ${xanoQuestion.reviewer_id}` : undefined,
      dueDate: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000), // Default 2 days
      reviews: []
    };
  });
  
  // If we're filtering client-side, adjust the totals
  const adjustedTotal = filter === 'pending' ? items.length : response.itemsTotal;
  
  return {
    items,
    totalItems: adjustedTotal,
    totalPages: Math.ceil(adjustedTotal / 30) // Assuming 30 items per page
  };
};

export const useReviewQueue = (filter: 'all' | 'pending' | 'in_progress', page: number = 1) => {
  // Use React Query for caching
  const {
    data,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['review-queue', filter, page],
    queryFn: () => fetchReviewQueue(filter, page),
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
    retry: 2,
    refetchOnWindowFocus: false,
    placeholderData: (previousData) => previousData, // Keep previous data while loading
    throwOnError: false
  });

  // Fallback to mock data on error
  const queueItems = data?.items || (() => {
    if (error) {
      const mockItems = Array.from({ length: 12 }, (_, i) => generateMockReviewItem(i));
      return filter === 'all' ? mockItems : mockItems.filter(item => item.status === filter);
    }
    return [];
  })();

  // Calculate stats - use totalItems from API for the current filter
  const stats = {
    pending: filter === 'pending' ? (data?.totalItems || 0) : queueItems.filter(item => item.status === 'pending').length,
    inProgress: filter === 'in_progress' ? (data?.totalItems || 0) : queueItems.filter(item => item.status === 'in_progress').length,
    completedToday: 3, // Mock
    dueToday: queueItems.filter(item => {
      if (!item.dueDate) return false;
      const today = new Date();
      return item.dueDate.toDateString() === today.toDateString();
    }).length,
    highPriority: queueItems.filter(item => item.priority === 'high' && item.status !== 'completed').length,
  };

  return {
    queueItems,
    isLoading,
    stats,
    error: error ? `Failed to load review queue: ${error instanceof Error ? error.message : 'Unknown error'}. Using mock data.` : null,
    totalItems: data?.totalItems || 0,
    totalPages: data?.totalPages || 1,
    currentPage: page,
    refetch
  };
};